import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, first, switchMap } from 'rxjs/operators';
import { SnackbarComponent } from 'shared/modules/skeleton/snackbar/snackbar.component';

export interface DispatchCases {
  default?: string;
  literalDefault?: string;

  [errorName: string]: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class NotifierService {
  constructor(private snackBar: MatSnackBar, private translateService: TranslateService) {}

  dispatchHttpError(response: HttpErrorResponse): void {
    const message = response.error?.message;
    if ((typeof message === 'string' && message.length)
      || (Array.isArray(message) && message.length && message.every(item => typeof item === 'string'))) {
      this.dispatch(message, 'error-color', false);
    }
    else {
      this.dispatchError();
    }
  }

  dispatchError(message?: string | string[], translate: boolean = true): void {
    this.dispatch(message, 'error-color', translate);
  }

  dispatchNotification(message: string, params?: Record<string, string | number>): void {
    this.dispatch(message, 'success-color', true, params);
  }

  switchCaseDispatch(type: string, errorCases: DispatchCases): void {
    const message = type === 'default' || type === 'literalDefault' ? undefined : errorCases[type];
    if (!message && errorCases.literalDefault !== undefined) {
      this.dispatchError(errorCases.literalDefault, false);
      return;
    }
    this.dispatchError(message || errorCases.default || 'ERROR.unexpected_error');
  }

  private dispatch(
    message: string | string[] | undefined,
    panelClass: 'success-color' | 'error-color',
    translate: boolean = true,
    params?: Record<string, string | number>,
  ): void {
    const content = message?.length ? message : 'ERROR.unexpected_error';
    const translated = translate || !message?.length;
    const ready: Observable<unknown> = this.translateService.currentLang ? of(null) : this.translateService.onLangChange.pipe(first());
    const result: Observable<string | string[] | Record<string, string>> = translated
      ? ready.pipe(switchMap(() => this.translateService.get(content, params)))
      : of(content);
    result.pipe(
      first(),
      catchError((error: unknown) => {
        console.error('Unable to load notification translations:', error);
        return EMPTY;
      }),
    )
      .subscribe((t) => {
        const msg = typeof t === 'string' || Array.isArray(t) ? t : Array.isArray(content) ? content.map(key => t[key]) : Object.values(t);

        this.snackBar.openFromComponent(SnackbarComponent, { data: msg, duration: 3000, panelClass });
      });
  }
}
